// ** Lazy Icon Loading System
// Tree-shaking friendly icon loader dla mniejszego bundle size

// #####################################################

/**
 * Dynamic icon registry dla tree-shaking
 * Ikony są ładowane tylko gdy są rzeczywiście używane
 */
const lazyIconRegistry = new Map();
const loadingPromises = new Map();

// #####################################################

/**
 * Register icon data dynamically
 * Umożliwia tree-shaking nieużywanych ikon
 */
export const registerIcon = (iconName, iconData) => {
	lazyIconRegistry.set(iconName, iconData);
};

// #####################################################

/**
 * Lazy load icon with code splitting
 * Ładuje ikonę tylko gdy jest potrzebna
 */
export const loadIcon = async (iconName) => {
	if (lazyIconRegistry.has(iconName)) {
		return lazyIconRegistry.get(iconName);
	}

	if (loadingPromises.has(iconName)) {
		return loadingPromises.get(iconName);
	}

	const loadPromise = (async () => {
		try {
			const { iconRegistry } = await import("../data/index.js");
			const iconData = iconRegistry[iconName];

			if (iconData) {
				lazyIconRegistry.set(iconName, iconData);
				return iconData;
			}

			throw new Error(`Icon "${iconName}" not found`);
		} catch (error) {
			console.warn(`Failed to load icon "${iconName}":`, error);
			return null;
		} finally {
			loadingPromises.delete(iconName);
		}
	})();

	loadingPromises.set(iconName, loadPromise);
	return loadPromise;
};

// #####################################################

/**
 * Batch preload multiple icons
 * Dla krytycznych ikon które mają być załadowane wcześnie
 */
export const preloadIcons = async (iconNames) => {
	const loadPromises = iconNames.map((iconName) => loadIcon(iconName));
	return Promise.allSettled(loadPromises);
};

// #####################################################

/**
 * Get icon synchronously if already loaded
 * Fallback do async loading jeśli nie załadowana
 */
export const getIcon = (iconName, variant = "solid") => {
	const iconData = lazyIconRegistry.get(iconName);

	if (!iconData) {
		loadIcon(iconName);
		return null;
	}

	return {
		viewBox: iconData.viewBox,
		path: iconData.variants?.[variant]?.path || iconData.path,
	};
};

// #####################################################

export default {
	registerIcon,
	loadIcon,
	preloadIcons,
	getIcon,
};

// #####################################################
